import { useMemo, useEffect } from 'react'
import { Group } from '@visx/group'
import { Orientation } from '@visx/axis'

import { colors } from '#base/js/variables.style'
import { Axis } from '../common/axis'
import { ChartSizer } from '../common/chart-sizer'
import { DynamicMarker } from './dynamic-marker'
import { LinePaths, Curve } from './line-paths'
import { StaticMarker } from './static-marker'
import { lineChartService, ScaleType } from './line-chart.service'

export { Curve, ScaleType }

export const LineChart = ({
  width,
  height,
  heightWidthRatio = 0.5,
  heightViewportRatio = 0.9,
  maxHeight,
  ...props
}) => (
  <ChartSizer
    width={width}
    height={height}
    heightWidthRatio={heightWidthRatio}
    heightViewportRatio={heightViewportRatio}
    maxHeight={maxHeight}
  >
    {({ width, height }) => {
      if (width === 0 && height === 0) {
        return null
      }

      return (
        <SizedLineChart
          width={width}
          height={height}
          {...props}
        />
      )
    }}
  </ChartSizer>
)

const SizedLineChart = ({
  width,
  height,
  marginTop = 20,
  marginRight = 20,
  marginBottom = 55,
  marginLeft = 75,
  series,
  scaleTypeX = ScaleType.Linear,
  scaleTypeY = ScaleType.Linear,
  boundsX,
  boundsY,
  labelX,
  labelY,
  formatterX,
  formatterY,
  tickValuesX,
  tickValuesY,
  curve,
  selectedX,
  markers,
  onMouseMove,
  onMouseLeave,
  onClick,
  onDimensionChange
}) => {
  const svg = { width, height }
  width = width - marginLeft - marginRight
  height = height - marginTop - marginBottom
  const domains = useMemo(() => lineChartService.calculateDomain(series, boundsX, boundsY), [series, boundsX, boundsY])
  const scaleX = lineChartService.buildScale(scaleTypeX, domains.x, [0, width])
  const scaleY = lineChartService.buildScale(scaleTypeY, domains.y, [height, 0])
  const showPoints = useMemo(() => lineChartService.shouldShowPoints(series, width), [series, width])
  const showHorizontalLineAtZero = useMemo(() => lineChartService.hasNegativeValues(domains.y), [domains])
  const showVerticalLineAtZero = useMemo(() => lineChartService.hasNegativeValues(domains.x), [domains])

  useEffect(() => {
    if (onDimensionChange) {
      onDimensionChange({ width, height })
    }
  }, [width, height, onDimensionChange])

  return (
    <svg width={svg.width} height={svg.height}>
      <Group top={marginTop} left={marginLeft}>
        {showHorizontalLineAtZero &&
          <line
            y1={scaleY(0)}
            y2={scaleY(0)}
            stroke={colors.gray}
            strokeWidth={0.5}
            x1={0}
            x2={width}
            data-qa="y-zero-line"
          />
        }
        {showVerticalLineAtZero &&
          <line
            y1={0}
            y2={height}
            stroke={colors.gray}
            strokeWidth={0.5}
            x1={scaleX(0)}
            x2={scaleX(0)}
            data-qa="x-zero-line"
          />
        }
        <Axis
          orientation={Orientation.bottom}
          top={height}
          width={width}
          scale={scaleX}
          label={labelX}
          tickFormat={formatterX}
          tickValues={tickValuesX}
          stroke={showHorizontalLineAtZero ? 'transparent' : undefined }
        />
        <Axis
          orientation={Orientation.left}
          height={height}
          scale={scaleY}
          label={labelY}
          tickFormat={formatterY}
          tickValues={tickValuesY}
          stroke={showVerticalLineAtZero ? 'transparent' : undefined }
        />
        <LinePaths
          series={series}
          scaleX={scaleX}
          scaleY={scaleY}
          curve={curve}
          showPoints={showPoints}
        />
        {markers?.map((marker, i) => {
          return (
            <StaticMarker
              key={i}
              label={marker.label}
              value={marker.x}
              scale={scaleX}
              height={height}
              selectedX={selectedX}
            />
          )
        })}
        <DynamicMarker
          series={series}
          scaleX={scaleX}
          scaleY={scaleY}
          width={width}
          height={height}
          marginLeft={marginLeft}
          selectedX={selectedX}
          formatter={formatterX}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
        />
      </Group>
    </svg>
  )
}
